import { INVOICE_HOST } from './invoices.js';
import { toIsoTimestamp } from './packages-wire.js';

/**
 * `get_trade` with `type=ALL` → the Wallet's transaction history (MYP-146), the old website's
 * 账户明细 · 所有交易.
 *
 * Every movement of the wallet's money: order charges (重量收费, 订单服务费, 附加费用…), manual
 * credits (补款), completed top-ups, refunds. `type=ALL` also returns every unfinished top-up
 * attempt (553 of 573 rows on the old test account), which moved no money; the old website's list
 * shows only what did, so only rows whose `status` is `END` are kept — the only status on which
 * the vendor says `balance` holds (Q&A v2, question 11).
 *
 * Read on purpose, and nothing else: rows also carry the FPX buyer's name and e-mail, the operator
 * and the uploaded slip's path. None of that is the customer's to see here, so none of it is copied.
 * `remark1` is staff-written and is shown, as the old website shows it.
 */

/** One movement of the wallet's money. */
export interface TradeRecord {
  readonly id: string;
  /** When it was processed, ISO 8601; undefined when the row carries no usable date. */
  readonly at?: string;
  /** The order or top-up number (`tradeno`). */
  readonly reference?: string;
  /** Whether `reference` is an order number (links to the order) rather than a top-up number. */
  readonly isOrder: boolean;
  /** The paying bank (`bankname`), on top-ups. */
  readonly bank?: string;
  /** Signed, in cents: positive is money in, negative money out. */
  readonly amountCents: number;
  /** As the vendor writes it (`RM`); `formatMoney` normalises it. */
  readonly currency: string;
  /** The balance after this movement, in cents. */
  readonly balanceCents?: number;
  /** The vendor's type code (`ORDERPAY`, `CHARGE`, …), kept for keys and tests. */
  readonly typeCode?: string;
  /** The type as the vendor names it, in the reader's language — never a guessed label. */
  readonly typeLabel: string;
  /** Staff's note (`remark1`), as the old website shows it. */
  readonly remark?: string;
  /** Weight fees only: the receipt (`receipturl`) — a vendor link or site path, see `tradeLink`. */
  readonly receiptUrl?: string;
  /** Weight fees only: the receipt as a PDF (`pdfurl`). */
  readonly pdfUrl?: string;
}

type Row = Readonly<Record<string, unknown>>;

function text(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** `"-58.50"` → -5850; a number works too. Undefined for anything that is not a number. */
function cents(value: unknown): number | undefined {
  const raw = text(value);
  if (raw === undefined || !/^[-+]?\d+(\.\d+)?$/.test(raw)) return undefined;
  return Math.round(Number(raw) * 100);
}

/**
 * A receipt or PDF link the app may hand the browser: a path on the vendor's site (`/Public/…`),
 * or an HTTPS link on the vendor's own host. Anything else — another host, plain HTTP, a
 * `javascript:` URL — is dropped rather than shown, as invoice links are (`invoiceUrlFrom`).
 */
export function tradeLink(value: unknown): string | undefined {
  const raw = text(value);
  if (raw === undefined) return undefined;
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return undefined;
  }
  return url.protocol === 'https:' && url.hostname === INVOICE_HOST ? url.toString() : undefined;
}

/** `CHARGE` rows are top-ups: their `tradeno` is a top-up number, not an order to link to. */
function isTopUpType(type: string | undefined): boolean {
  return type?.toUpperCase() === 'CHARGE';
}

/**
 * The settled movements, newest first. `language` picks `type_cn` or `type_en`; the code itself is
 * the last resort, so an unknown type is shown as sent rather than mislabelled.
 */
export function normaliseTrades(
  rows: readonly unknown[],
  language: 'zh' | 'en',
): readonly TradeRecord[] {
  const out: TradeRecord[] = [];

  for (const [index, raw] of rows.entries()) {
    if (typeof raw !== 'object' || raw === null) continue;
    const row = raw as Row;
    if (text(row['status'])?.toUpperCase() !== 'END') continue;

    const amountCents = cents(row['amount']);
    if (amountCents === undefined) continue;

    const typeCode = text(row['type']);
    const typeLabel =
      (language === 'zh'
        ? (text(row['type_cn']) ?? text(row['type_en']))
        : (text(row['type_en']) ?? text(row['type_cn']))) ??
      typeCode ??
      '—';
    const reference = text(row['tradeno']) ?? text(row['rechargeno']);
    const remark = text(row['remark1']);
    const bank = text(row['bankname']);
    const balanceCents = cents(row['balance']);
    const receiptUrl = tradeLink(row['receipturl']);
    const pdfUrl = tradeLink(row['pdfurl']);
    const at =
      toIsoTimestamp(row['handledate']) ??
      toIsoTimestamp(row['chargedate']) ??
      toIsoTimestamp(row['submitdate']);

    out.push({
      id: text(row['id']) ?? `${reference ?? 'row'}-${index}`,
      ...(at ? { at } : {}),
      ...(reference ? { reference } : {}),
      isOrder: reference !== undefined && !isTopUpType(typeCode),
      ...(bank ? { bank } : {}),
      amountCents,
      currency: text(row['currency']) ?? 'RM',
      ...(balanceCents === undefined ? {} : { balanceCents }),
      ...(typeCode ? { typeCode } : {}),
      typeLabel,
      ...(remark ? { remark } : {}),
      ...(receiptUrl ? { receiptUrl } : {}),
      ...(pdfUrl ? { pdfUrl } : {}),
    });
  }

  // Newest first; rows with no date keep their place at the end, in the vendor's order.
  return out
    .map((record, order) => ({ record, order }))
    .sort((a, b) => {
      if (a.record.at && b.record.at) {
        const byDate = b.record.at.localeCompare(a.record.at);
        return byDate !== 0 ? byDate : a.order - b.order;
      }
      if (a.record.at) return -1;
      if (b.record.at) return 1;
      return a.order - b.order;
    })
    .map(({ record }) => record);
}
